import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom';
import { setFocus, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import { type TransformedChannel, useChannelStore } from '@/stores/channelStore';
import { dialogFocusLock } from '@/utils/dialogFocusLock';
import type { HighlightState, NavScrollState, NavigationHandlers, NavigationState } from './types';
import {
  NAV_DEBOUNCE_DELAY,
  FOCUS_CHECK_INTERVAL,
  ROUTE_FOCUS_DELAY,
  NAV_SCROLL_DELAY,
  SCROLL_UPDATE_DELAY,
  DOM_UPDATE_DELAY,
} from './constants';
import {
  getCurrentNavIndex,
  calculateNavItemScroll,
  calculateNavScrollState,
  calculateHighlightPosition,
} from './utils';

// 全局记录上一次路径，防止 Header 在搜索页卸载时丢失
let globalPreviousPath = '';

/**
 * 导航相关的 Hook（焦点、高亮、滚动）
 */
export function useNavigation(skipAutoFocus = false) {
  const navigate = useNavigate();
  const location = useLocation();
  const channelList = useChannelStore((state) => state.channelList);

  // 导航容器引用
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navScrollRef = useRef<HTMLDivElement>(null);

  // 路由切换防抖定时器
  const navDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 焦点切换防抖定时器（用于延迟高亮和状态更新，与路由同步）
  const focusDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 标记是否正在快速切换导航项（防抖期间）
  const isQuickSwitchingRef = useRef<boolean>(false);

  // 快速切换保护定时器（用于延迟清除快速切换状态）
  const quickSwitchProtectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 存储最新的 pathname，避免防抖回调中闭包问题
  const locationPathnameRef = useRef(location.pathname);
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // 记住最近一次在 Header 中获焦的元素
  const lastFocusedKeyRef = useRef<string>('nav-search');

  // 记录上一个路由，用于返回时恢复焦点（使用全局变量防卸载重置）
  const previousPathRef = useRef(globalPreviousPath || location.pathname);

  // 标记是否刚从搜索页返回，用于阻止频道页自动聚焦
  const isReturningFromSearchRef = useRef(false);

  // 缓存上一次的焦点 key，避免重复处理
  const lastCheckedFocusKeyRef = useRef<string>('');

  // 状态
  const [focusedNavItem, setFocusedNavItem] = useState<TransformedChannel>();
  const [focusedNavKey, setFocusedNavKey] = useState<string>('');
  const [highlightState, setHighlightState] = useState<HighlightState>({
    left: 0,
    width: 0,
    visible: false,
  });
  const [isNavFocused, setIsNavFocused] = useState(true);
  const [navScrollState, setNavScrollState] = useState<NavScrollState>({
    showLeftGradient: false,
    showRightGradient: false,
    scrollLeft: 0,
  });

  // 构建导航项
  const navItems = useMemo(() => {
    if (!channelList?.length) {
      return [];
    }
    return channelList.map((channel) => ({
      ...channel,
      path: `/channel/${channel.channelId}/${channel.channelType}`,
      label: channel.title,
    }));
  }, [channelList]);

  /**
   * 更新高亮层位置
   */
  const updateHighlightPosition = useCallback((navFocusKey: string) => {
    const position = calculateHighlightPosition(navFocusKey, navContainerRef.current);
    setHighlightState(position);
  }, []);

  /**
   * 更新导航滚动状态
   */
  const updateNavScrollState = useCallback(() => {
    if (!navScrollRef.current) return;
    const state = calculateNavScrollState(navScrollRef.current);
    setNavScrollState(state);
  }, []);

  /**
   * 获焦导航项时自动滚动到可见区域
   */
  const scrollNavItemIntoView = useCallback(
    (navFocusKey: string) => {
      if (!navScrollRef.current) return;

      const container = navScrollRef.current;
      const newScrollLeft = calculateNavItemScroll(navFocusKey, container);

      if (newScrollLeft === null) return;

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'auto',
      });

      // 立即更新滚动状态
      updateNavScrollState();
    },
    [updateNavScrollState]
  );

  /**
   * 公共防抖执行函数 - 用于高亮更新和路由导航
   * @param highlightCallback 高亮更新回调
   * @param routeCallback 路由导航回调
   */
  const debouncedNavAction = useCallback((highlightCallback: () => void, routeCallback: () => void) => {
    // 清除之前的焦点防抖定时器
    if (focusDebounceTimerRef.current) {
      clearTimeout(focusDebounceTimerRef.current);
    }

    // 清除之前的路由防抖定时器
    if (navDebounceTimerRef.current) {
      clearTimeout(navDebounceTimerRef.current);
    }

    // 清除之前的快速切换保护定时器
    if (quickSwitchProtectionTimerRef.current) {
      clearTimeout(quickSwitchProtectionTimerRef.current);
      quickSwitchProtectionTimerRef.current = null;
    }

    // 标记开始快速切换
    isQuickSwitchingRef.current = true;

    // 使用防抖延迟更新高亮
    focusDebounceTimerRef.current = setTimeout(() => {
      highlightCallback();
      focusDebounceTimerRef.current = null;
    }, NAV_DEBOUNCE_DELAY);

    // 使用相同延迟执行路由导航
    navDebounceTimerRef.current = setTimeout(() => {
      routeCallback();
      navDebounceTimerRef.current = null;

      // 路由导航完成后，延迟 200ms 清除快速切换状态
      // 这样可以保护在快速切换后短暂时间内焦点的移动
      quickSwitchProtectionTimerRef.current = setTimeout(() => {
        isQuickSwitchingRef.current = false;
        quickSwitchProtectionTimerRef.current = null;
      }, 200); // 200ms 保护时间窗口
    }, NAV_DEBOUNCE_DELAY);
  }, []);

  /**
   * 处理导航聚焦
   * - 高亮更新和路由导航都使用防抖，确保与页面内容加载同步
   * - 避免"高亮快速移动但页面内容延迟加载"导致的不同步体验
   */
  const handleNavFocus = useCallback(
    (path: string, navFocusKey: string, navItem: TransformedChannel) => {
      lastFocusedKeyRef.current = navFocusKey;
      setIsNavFocused(true);

      debouncedNavAction(
        // 高亮更新回调
        () => {
          setFocusedNavItem(navItem);
          setFocusedNavKey(navFocusKey);
          updateHighlightPosition(navFocusKey);

          setTimeout(() => {
            scrollNavItemIntoView(navFocusKey);
          }, NAV_SCROLL_DELAY);
        },
        // 路由导航回调
        () => {
          const currentPathname = locationPathnameRef.current;
          if (currentPathname === path || currentPathname.startsWith(`${path}/`)) {
            return;
          }
          navigate(path);
        }
      );
    },
    [navigate, scrollNavItemIntoView, updateHighlightPosition, debouncedNavAction]
  );

  /**
   * 处理导航点击
   */
  /**
   * 处理导航点击
   */
  const handleNavClick = useCallback(
    (path: string) => {
      if (path.startsWith('/channel/')) {
        const filterPageParam = focusedNavItem?.filterPageParam;
        const channelId = focusedNavItem?.channelId ?? '';
        const channelType = focusedNavItem?.channelType ?? '';
        // 根据后台下发的tab中的filterPageParam参数，设置跳转后的初始请求参数
        // 如果没有默认first_category=0，筛选全部
        const searchParams =
          (filterPageParam ? createSearchParams(filterPageParam).toString() : '') || 'first_category=0';
        navigate(`/more?from=channel&channelId=${channelId}&channelType=${channelType}&${searchParams}`);
        return;
      }
    },
    [navigate, focusedNavItem]
  );

  /**
   * 处理导航方向键
   */
  const handleNavArrowPress = useCallback((direction: string) => {
    if (direction === 'down') {
      // 🔑 关键优化：向下按键时立即清除导航高亮，不等待焦点检测
      setIsNavFocused(false);
      return true;
    }
    if (direction === 'up') {
      return false;
    }
    if (direction === 'left' || direction === 'right') {
      return true;
    }
    return true;
  }, []);

  /**
   * 处理"更多"按钮聚焦
   * - 高亮更新和路由导航都使用防抖，与 handleNavFocus 保持一致
   */
  const handleMoreFocus = useCallback(() => {
    lastFocusedKeyRef.current = 'nav-more';
    setIsNavFocused(true);

    debouncedNavAction(
      // 高亮更新回调
      () => {
        setFocusedNavItem(undefined);
        setFocusedNavKey('nav-more');
        updateHighlightPosition('nav-more');

        setTimeout(() => {
          scrollNavItemIntoView('nav-more');
        }, NAV_SCROLL_DELAY);
      },
      // 路由导航回调
      () => {
        const currentPathname = locationPathnameRef.current;
        if (!currentPathname.startsWith('/more')) {
          navigate('/more?first_category=0');
        }
      }
    );
  }, [scrollNavItemIntoView, navigate, updateHighlightPosition, debouncedNavAction]);

  /**
   * 处理"更多"按钮点击
   */
  const handleMoreClick = useCallback(() => {
    navigate('/more?first_category=0');
  }, [navigate]);

  /**
   * 处理搜索按钮聚焦
   */
  const handleSearchFocus = useCallback(() => {
    lastFocusedKeyRef.current = 'nav-search';
    setIsNavFocused(false);
  }, []);

  /**
   * 处理搜索按钮点击
   */
  const handleSearchClick = useCallback(() => {
    // 先记录“上一个路径”为 /search，方便返回时识别
    previousPathRef.current = '/search';
    globalPreviousPath = '/search';
    navigate('/search');
  }, [navigate]);

  /**
   * 处理导航滚动
   */
  const handleNavScroll = useCallback(() => {
    requestAnimationFrame(() => {
      updateNavScrollState();
    });
  }, [updateNavScrollState]);

  // 监听导航容器的 focusout 事件，实现焦点离开时的立即响应
  useEffect(() => {
    const handleFocusOut = (event: FocusEvent) => {
      // 使用 relatedTarget 判断焦点是否真的离开了导航区域
      const relatedTarget = event.relatedTarget as HTMLElement;
      const navContainer = navContainerRef.current;

      if (!navContainer) return;

      // 如果焦点移动到导航容器外的元素（或没有新焦点），立即清除高亮
      if (relatedTarget && !navContainer.contains(relatedTarget)) {
        // 检查焦点是否移到了导航项之外的元素
        const targetFocusKey = relatedTarget.getAttribute('data-focus-key');
        const isNavItem = targetFocusKey?.startsWith('nav-channel-') || targetFocusKey === 'nav-more';

        if (!isNavItem && isNavFocused) {
          setIsNavFocused(false);
        }
      }
    };

    const navContainer = navContainerRef.current;
    if (navContainer) {
      navContainer.addEventListener('focusout', handleFocusOut as EventListener);
      return () => {
        navContainer.removeEventListener('focusout', handleFocusOut as EventListener);
      };
    }
  }, [isNavFocused]);

  useEffect(() => {
    updateHighlightPosition(focusedNavKey);
  }, [highlightState.visible]);

  // 使用定时器定期检查焦点是否在 Header 内
  useEffect(() => {
    const checkFocus = () => {
      const currentFocusKey = getCurrentFocusKey();
      // debugger;
      if (skipAutoFocus) return;
      if (!currentFocusKey || currentFocusKey === lastCheckedFocusKeyRef.current) {
        return;
      }

      lastCheckedFocusKeyRef.current = currentFocusKey;

      const isHeaderElement = currentFocusKey === 'header';
      const isHeaderChild = currentFocusKey.startsWith('header-');
      const isNavElement = currentFocusKey.startsWith('nav-');
      const isInHeader = isHeaderElement || isHeaderChild || isNavElement;

      // 判断当前焦点是否在导航项（频道或更多按钮）上
      const isNavChannel = currentFocusKey.startsWith('nav-channel-');
      const isMoreButton = currentFocusKey === 'nav-more';
      const isOnNavItem = isNavChannel || isMoreButton;

      // 🔑 关键优化：如果焦点已经离开 Header 区域，立即更新 isNavFocused 状态
      // 这样可以让高亮层立即变为半透明，不受防抖保护的影响
      if (!isInHeader && isNavFocused) {
        setIsNavFocused(false);
        // 不启动保护窗口，让高亮层立即响应
        return;
      }

      // 🔑 关键优化：如果正在快速切换导航项，并且焦点不在导航项上，则忽略此次检查
      // 这样可以避免在快速切换过程中，焦点短暂离开 navItems 时触发路由拉回
      if (isQuickSwitchingRef.current && !isOnNavItem && isInHeader) {
        return;
      }

      // 特殊处理：如果焦点在 nav-more 上，但 focusedNavKey 不是 nav-more，需要手动更新
      if (isMoreButton && focusedNavKey !== 'nav-more') {
        setFocusedNavKey('nav-more');
        setFocusedNavItem(undefined);
        updateHighlightPosition('nav-more');
        lastFocusedKeyRef.current = 'nav-more';
        setIsNavFocused(true);

        // 滚动到可视区域
        setTimeout(() => {
          scrollNavItemIntoView('nav-more');
        }, NAV_SCROLL_DELAY);

        return; // 提前返回，避免后续逻辑干扰
      }

      if (isInHeader && focusedNavKey) {
        const isFocusedNavItem = currentFocusKey === focusedNavKey;
        const shouldHighlight = isFocusedNavItem || isMoreButton;

        // 只有在导航项之间切换时才更新高亮状态
        // 如果焦点切到非导航项元素（如个人中心、VIP按钮、搜索按钮），则不更新高亮
        if (isOnNavItem) {
          if (shouldHighlight && !isNavFocused) {
            setIsNavFocused(true);
          }
        } else {
          // 焦点在 Header 内但不在导航项上（如个人中心、VIP、搜索）
          if (isNavFocused) {
            setIsNavFocused(false);

            // 🔑 关键优化：焦点从导航项切换到非导航项时，启动快速切换保护
            // 防止路由监听器立即拉回焦点
            if (quickSwitchProtectionTimerRef.current) {
              clearTimeout(quickSwitchProtectionTimerRef.current);
            }
            isQuickSwitchingRef.current = true;
            quickSwitchProtectionTimerRef.current = setTimeout(() => {
              isQuickSwitchingRef.current = false;
              quickSwitchProtectionTimerRef.current = null;
            }, 300); // 300ms 保护时间窗口
          }
        }
      }
    };

    const interval = setInterval(checkFocus, FOCUS_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [isNavFocused, focusedNavKey, updateHighlightPosition, scrollNavItemIntoView]);

  // 当获焦的导航项变化时，更新高亮层位置
  useEffect(() => {
    if (focusedNavKey) {
      updateHighlightPosition(focusedNavKey);
    } else {
      setHighlightState((prev) => ({ ...prev, visible: false }));
    }
  }, [focusedNavKey, updateHighlightPosition]);

  // 滚动时更新高亮层位置
  useEffect(() => {
    if (focusedNavKey && navScrollRef.current) {
      const handleScroll = () => {
        updateHighlightPosition(focusedNavKey);
      };
      const scrollContainer = navScrollRef.current;
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [focusedNavKey, updateHighlightPosition]);

  // 当路由变化时，设置焦点到对应的导航项
  useEffect(() => {
    const prevPath = previousPathRef.current;
    const currentPath = location.pathname;
    const isFromSearch = prevPath === '/search';
    const isCurrentChannel = currentPath.startsWith('/channel');

    // 从搜索页返回频道页，启动焦点恢复逻辑
    if (isFromSearch && isCurrentChannel) {
      if (skipAutoFocus) {
        return;
      }
      isReturningFromSearchRef.current = true;

      // **提前更新 previousPathRef，避免多次触发，并写入全局**
      previousPathRef.current = currentPath;
      globalPreviousPath = currentPath;

      // 立即启动恢复逻辑
      const targetKey = lastFocusedKeyRef.current || 'nav-search';

      // **立即执行第一次恢复（不延迟，抢在其他逻辑之前）**
      try {
        // 检查焦点锁，如果弹窗正在显示则跳过
        if (dialogFocusLock.isLocked()) {
          return;
        }

        setFocus(targetKey);
        if (targetKey === 'nav-search') {
          setIsNavFocused(false);
        } else {
          setIsNavFocused(true);
        }
      } catch {
        // 立即恢复焦点失败，继续执行延迟恢复
      }

      // 第二次尝试，确保不被覆盖（使用定时器）
      const restoreTimer = setTimeout(() => {
        try {
          // 检查焦点锁，如果弹窗正在显示则跳过
          if (dialogFocusLock.isLocked()) {
            return;
          }
          setFocus(targetKey);
          if (targetKey === 'nav-search') {
            setIsNavFocused(false);
          } else {
            setIsNavFocused(true);
          }
          // 重置标记
          isReturningFromSearchRef.current = false;
        } catch {
          // 延迟恢复焦点失败，忽略
        }
      }, 300);

      return () => {
        clearTimeout(restoreTimer);
      };
    }

    if (currentPath === '/search' || currentPath.startsWith('/more')) {
      previousPathRef.current = currentPath;
      globalPreviousPath = currentPath;
      return;
    }

    // 当频道列表为空时，设置焦点到搜索按钮，确保用户可以正常交互
    if (!navItems || navItems.length === 0) {
      previousPathRef.current = currentPath;
      globalPreviousPath = currentPath;

      const timer = setTimeout(() => {
        if (skipAutoFocus) {
          return;
        }
        try {
          if (dialogFocusLock.isLocked()) {
            return;
          }
          setFocus('nav-search');
          lastFocusedKeyRef.current = 'nav-search';
          setIsNavFocused(false);
        } catch {
          // 设置搜索按钮焦点失败，忽略
        }
      }, ROUTE_FOCUS_DELAY);

      return () => clearTimeout(timer);
    }

    // 如果是从搜索页返回，不自动设置频道焦点，让上面的恢复逻辑处理
    if (isReturningFromSearchRef.current) {
      previousPathRef.current = currentPath;
      globalPreviousPath = currentPath;
      return;
    }

    // 🔑 关键优化：如果正在快速切换导航项，跳过路由自动聚焦
    // 避免在用户快速切换过程中，焦点被路由监听器拉回
    if (isQuickSwitchingRef.current) {
      return;
    }

    const currentNavIndex = getCurrentNavIndex(navItems, location);
    const currentNavItem = navItems[currentNavIndex];

    if (!currentNavItem) {
      previousPathRef.current = currentPath;
      return;
    }

    const focusedItemId = currentNavItem.channelId;
    const focusedItemType = currentNavItem.channelType;
    const navFocusKey = `nav-channel-${focusedItemId}-${focusedItemType}`;

    // **同步更新 focusedNavItem 状态**
    setFocusedNavItem(currentNavItem);
    setFocusedNavKey(navFocusKey);

    const timer = setTimeout(() => {
      try {
        // 检查焦点锁，如果弹窗正在显示则跳过
        if (dialogFocusLock.isLocked()) {
          return;
        }
        if (skipAutoFocus) {
          return;
        }
        setFocus(navFocusKey);
        lastFocusedKeyRef.current = navFocusKey;
        setIsNavFocused(true);
      } catch {
        // 设置焦点失败，忽略错误
      }
    }, ROUTE_FOCUS_DELAY);

    previousPathRef.current = currentPath;
    globalPreviousPath = currentPath;

    return () => clearTimeout(timer);
  }, [location.pathname, navItems, setIsNavFocused]);

  // 路由变化时，根据当前路由判断对应的 navItem 是否被遮挡，并自动滚动
  useEffect(() => {
    const currentNavIndex = getCurrentNavIndex(navItems, location);
    const navItem = navItems[currentNavIndex];

    if (!navItem || location.pathname !== navItem.path) {
      return;
    }

    const timer = setTimeout(() => {
      if (!navScrollRef.current) return;
      const navFocusKey = `nav-channel-${navItem.channelId}-${navItem.channelType}`;
      const newScrollLeft = calculateNavItemScroll(navFocusKey, navScrollRef.current);

      if (newScrollLeft !== null) {
        navScrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'auto' });
        setTimeout(() => {
          updateNavScrollState();
        }, SCROLL_UPDATE_DELAY);
      }
    }, DOM_UPDATE_DELAY);

    return () => clearTimeout(timer);
  }, [location.pathname, updateNavScrollState, navItems]);

  // 初始化导航滚动状态
  useEffect(() => {
    updateNavScrollState();
    const resizeObserver = new ResizeObserver(() => {
      updateNavScrollState();
    });
    if (navScrollRef.current) {
      resizeObserver.observe(navScrollRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [updateNavScrollState]);

  // 清除所有防抖定时器的函数
  const clearAllDebounceTimers = useCallback(() => {
    if (navDebounceTimerRef.current) {
      clearTimeout(navDebounceTimerRef.current);
      navDebounceTimerRef.current = null;
    }
    if (focusDebounceTimerRef.current) {
      clearTimeout(focusDebounceTimerRef.current);
      focusDebounceTimerRef.current = null;
    }
    if (quickSwitchProtectionTimerRef.current) {
      clearTimeout(quickSwitchProtectionTimerRef.current);
      quickSwitchProtectionTimerRef.current = null;
    }
    // 清除快速切换状态
    isQuickSwitchingRef.current = false;
  }, []);

  // 监听全局弹窗显示事件，清除防抖定时器避免焦点冲突
  useEffect(() => {
    const handleDialogShow = () => {
      clearAllDebounceTimers();
    };

    window.addEventListener('confirm-dialog-show', handleDialogShow);
    return () => {
      window.removeEventListener('confirm-dialog-show', handleDialogShow);
    };
  }, [clearAllDebounceTimers]);

  // 组件卸载时清理防抖定时器
  useEffect(() => {
    return () => {
      clearAllDebounceTimers();
    };
  }, [clearAllDebounceTimers]);

  const handlers: NavigationHandlers = {
    handleNavFocus,
    handleNavClick,
    handleNavArrowPress,
    handleMoreFocus,
    handleMoreClick,
    handleSearchFocus,
    handleSearchClick,
  };

  const state: NavigationState = {
    focusedNavItem,
    focusedNavKey,
    highlightState,
    isNavFocused,
    navScrollState,
  };

  return {
    navItems,
    navContainerRef,
    navScrollRef,
    handlers,
    state,
    handleNavScroll,
    lastFocusedKeyRef,
    setIsNavFocused,
  };
}
